function quote(value) {
    return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}
function quoteList(values) {
    if (values.length === 1)
        return quote(values[0]);
    return `[${values.map(quote).join(", ")}]`;
}
function matchTypeTag(match) {
    switch (match) {
        case "contains":
            return ":contains";
        case "matches":
            return ":matches";
        case "regex":
            return ":regex";
        case "value":
            return ":value";
        case "count":
            return ":count";
        case "is":
        default:
            return ":is";
    }
}
function emitComparator(match) {
    const parts = [];
    if (match.addressPart && match.addressPart !== "all") {
        parts.push(`:${match.addressPart}`);
    }
    if (match.comparator) {
        parts.push(`:comparator ${quote(match.comparator)}`);
    }
    const tag = matchTypeTag(match.matchType);
    if (match.matchType === "value" || match.matchType === "count") {
        parts.push(tag);
        if (match.relational)
            parts.push(quote(match.relational));
    }
    else if (match.matchType && match.matchType !== "is") {
        parts.push(tag);
    }
    else if (!match.matchType || match.matchType === "is") {
        parts.push(":is");
    }
    return parts.join(" ");
}
function collectRequiresFromTest(test, req) {
    switch (test.type) {
        case "not":
            collectRequiresFromTest(test.test, req);
            break;
        case "allof":
        case "anyof":
            for (const t of test.tests)
                collectRequiresFromTest(t, req);
            break;
        case "envelope":
            req.add("envelope");
            break;
        case "body":
            req.add("body");
            break;
        case "date":
            req.add("date");
            break;
        case "mailboxexists":
            req.add("mailbox");
            break;
        case "duplicate":
            req.add("duplicate");
            break;
        case "spamtest":
            req.add("spamtest");
            break;
        case "virustest":
            req.add("virustest");
            break;
        case "metadataexists":
        case "servermetadataexists":
        case "metadata":
        case "servermetadata":
            req.add(test.type.includes("server") ? "servermetadata" : "mboxmetadata");
            break;
        default:
            break;
    }
    if ((test.type === "header" ||
        test.type === "address" ||
        test.type === "envelope" ||
        test.type === "body" ||
        test.type === "date" ||
        test.type === "spamtest" ||
        test.type === "virustest") &&
        "match" in test) {
        if (test.match.matchType === "regex")
            req.add("regex");
        if (test.match.matchType === "value" || test.match.matchType === "count") {
            req.add("relational");
        }
        if (test.match.addressPart && test.match.addressPart !== "all") {
            // address parts are core for address test
        }
    }
}
function collectRequiresFromAction(action, req) {
    switch (action.type) {
        case "fileinto":
            req.add("fileinto");
            if (action.copy)
                req.add("copy");
            if (action.create)
                req.add("mailbox");
            break;
        case "redirect":
            if (action.copy)
                req.add("copy");
            break;
        case "reject":
            req.add("reject");
            break;
        case "vacation":
            req.add("vacation");
            if (action.seconds != null)
                req.add("vacation-seconds");
            break;
        case "addflag":
        case "setflag":
        case "removeflag":
            req.add("imap4flags");
            break;
        case "set":
            req.add("variables");
            break;
        case "include":
            req.add("include");
            break;
        case "notify":
            req.add("enotify");
            break;
        case "addheader":
        case "deleteheader":
            req.add("editheader");
            break;
        default:
            break;
    }
}
function collectRequiresFromStatements(statements, req) {
    for (const stmt of statements) {
        if (stmt.type === "if") {
            collectRequiresFromTest(stmt.test, req);
            collectRequiresFromStatements(stmt.then, req);
            for (const branch of stmt.elsif) {
                collectRequiresFromTest(branch.test, req);
                collectRequiresFromStatements(branch.then, req);
            }
            if (stmt.else)
                collectRequiresFromStatements(stmt.else, req);
        }
        else if (stmt.type === "action") {
            collectRequiresFromAction(stmt.action, req);
        }
        else if (stmt.type === "require") {
            for (const e of stmt.extensions)
                req.add(e);
        }
    }
}
function emitTest(test) {
    switch (test.type) {
        case "true":
            return "true";
        case "false":
            return "false";
        case "not":
            return `not ${emitTest(test.test)}`;
        case "allof":
            return `allof (${test.tests.map(emitTest).join(", ")})`;
        case "anyof":
            return `anyof (${test.tests.map(emitTest).join(", ")})`;
        case "header":
            return `header ${emitComparator(test.match)} ${quoteList(test.headers)} ${quoteList(test.keys)}`;
        case "address":
            return `address ${emitComparator(test.match)} ${quoteList(test.headers)} ${quoteList(test.keys)}`;
        case "envelope":
            return `envelope ${emitComparator(test.match)} ${quoteList(test.fields)} ${quoteList(test.keys)}`;
        case "size":
            if (test.over != null)
                return `size :over ${test.over}`;
            if (test.under != null)
                return `size :under ${test.under}`;
            return "size :over 0";
        case "exists":
            return `exists ${quoteList(test.headers)}`;
        case "body": {
            const transform = test.bodyTransform && test.bodyTransform !== "text"
                ? `:${test.bodyTransform} `
                : "";
            return `body ${transform}${emitComparator(test.match)} ${quoteList(test.keys)}`;
        }
        case "date": {
            const zone = test.originalzone
                ? ":originalzone "
                : test.zone
                    ? `:zone ${quote(test.zone)} `
                    : "";
            return `date ${zone}${emitComparator(test.match)} ${quote(test.header)} ${quote(test.datePart)} ${quoteList(test.keys)}`;
        }
        case "mailboxexists":
            return `mailboxexists ${quoteList(test.mailboxes)}`;
        case "duplicate": {
            const parts = ["duplicate"];
            if (test.handle)
                parts.push(`:handle ${quote(test.handle)}`);
            if (test.header)
                parts.push(`:header ${quote(test.header)}`);
            if (test.uniqueid)
                parts.push(`:uniqueid ${quote(test.uniqueid)}`);
            if (test.seconds != null)
                parts.push(`:seconds ${test.seconds}`);
            return parts.join(" ");
        }
        case "spamtest":
        case "virustest":
            return `${test.type} ${emitComparator(test.match)} ${quoteList(test.keys)}`;
        case "metadataexists":
        case "servermetadataexists":
        case "metadata":
        case "servermetadata":
            return test.raw;
        case "opaque":
            return test.text;
    }
}
function emitTextBlock(message) {
    const normalized = message.replace(/\r\n/g, "\n");
    return `text:\n${normalized}${normalized.endsWith("\n") ? "" : "\n"}.`;
}
function emitAction(action) {
    switch (action.type) {
        case "keep":
            return "keep;";
        case "discard":
            return "discard;";
        case "stop":
            return "stop;";
        case "redirect":
            return `redirect${action.copy ? " :copy" : ""} ${quote(action.address)};`;
        case "reject":
            return `reject ${quote(action.reason)};`;
        case "fileinto": {
            const flags = [
                action.copy ? ":copy" : "",
                action.create ? ":create" : "",
            ]
                .filter(Boolean)
                .join(" ");
            return `fileinto${flags ? ` ${flags}` : ""} ${quote(action.mailbox)};`;
        }
        case "vacation": {
            const parts = ["vacation"];
            if (action.seconds != null)
                parts.push(`:seconds ${action.seconds}`);
            else if (action.days != null)
                parts.push(`:days ${action.days}`);
            if (action.subject)
                parts.push(`:subject ${quote(action.subject)}`);
            if (action.addresses?.length === 1) {
                parts.push(`:addresses ${quote(action.addresses[0])}`);
            }
            else if (action.addresses && action.addresses.length > 1) {
                parts.push(`:addresses ${quoteList(action.addresses)}`);
            }
            parts.push(emitTextBlock(action.message));
            return `${parts.join(" ")};`;
        }
        case "addflag":
        case "setflag":
        case "removeflag":
            return `${action.type} ${quoteList(action.flags)};`;
        case "set": {
            const mods = (action.modifiers ?? []).map((m) => `:${m}`).join(" ");
            return `set${mods ? ` ${mods}` : ""} ${quote(action.name)} ${quote(action.value)};`;
        }
        case "include": {
            const flags = [
                `:${action.location}`,
                action.optional ? ":optional" : "",
                action.once ? ":once" : "",
            ]
                .filter(Boolean)
                .join(" ");
            return `include ${flags} ${quote(action.script)};`;
        }
        case "notify": {
            const parts = ["notify"];
            if (action.from)
                parts.push(`:from ${quote(action.from)}`);
            if (action.importance)
                parts.push(`:importance ${quote(action.importance)}`);
            if (action.options?.length)
                parts.push(`:options ${quoteList(action.options)}`);
            if (action.message)
                parts.push(`:message ${quote(action.message)}`);
            parts.push(quote(action.method));
            return `${parts.join(" ")};`;
        }
        case "addheader":
        case "deleteheader":
            return action.raw.endsWith(";") ? action.raw : `${action.raw};`;
        case "opaque":
            return action.text.endsWith(";") ? action.text : `${action.text};`;
    }
}
function emitBlock(statements, indent) {
    if (statements.length === 0)
        return `${indent}{\n${indent}}\n`;
    const body = statements.map((s) => emitStatement(s, indent + "  ")).join("");
    return `${indent}{\n${body}${indent}}\n`;
}
function emitStatement(stmt, indent = "") {
    switch (stmt.type) {
        case "comment":
            return `${indent}# ${stmt.text}\n`;
        case "require":
            return `${indent}require [${stmt.extensions.map(quote).join(", ")}];\n`;
        case "opaque":
            return `${indent}${stmt.text}${stmt.text.endsWith("\n") ? "" : "\n"}`;
        case "action":
            return `${indent}${emitAction(stmt.action)}\n`;
        case "if": {
            let out = `${indent}if ${emitTest(stmt.test)}\n`;
            out += emitBlock(stmt.then, indent);
            for (const branch of stmt.elsif) {
                out += `${indent}elsif ${emitTest(branch.test)}\n`;
                out += emitBlock(branch.then, indent);
            }
            if (stmt.else) {
                out += `${indent}else\n`;
                out += emitBlock(stmt.else, indent);
            }
            return out;
        }
    }
}
/** Strip managed vacation include / require so builders can re-attach them. */
export function splitManagedVacationHeader(source) {
    const lines = source.split(/\r?\n/);
    const headerLines = [];
    let i = 0;
    let vacationEnabled = null;
    // consume leading requires and vacation includes/comments
    while (i < lines.length) {
        const line = lines[i];
        const trimmed = line.trim();
        if (!trimmed) {
            headerLines.push(line);
            i += 1;
            continue;
        }
        if (/^require\s*\[/.test(trimmed)) {
            headerLines.push(line);
            i += 1;
            continue;
        }
        const includeMatch = trimmed.match(/^#?\s*include\s+:personal\s+"vacation"\s*;/);
        if (includeMatch) {
            vacationEnabled = !trimmed.startsWith("#");
            headerLines.push(line);
            i += 1;
            continue;
        }
        if (trimmed === "# --- user rules below ---") {
            headerLines.push(line);
            i += 1;
            break;
        }
        break;
    }
    // Only treat as managed header if we saw a vacation include line
    if (vacationEnabled === null && headerLines.every((l) => !/include\s+:personal\s+"vacation"/.test(l))) {
        return { header: "", body: source, vacationEnabled: null };
    }
    return {
        header: headerLines.join("\n").trimEnd(),
        body: lines.slice(i).join("\n").replace(/^\n+/, ""),
        vacationEnabled,
    };
}
export function generateScript(ast, options) {
    const req = new Set();
    collectRequiresFromStatements(ast.statements, req);
    // Remove explicit require statements from output body; we emit one combined require.
    const bodyStatements = ast.statements.filter((s) => s.type !== "require");
    const body = bodyStatements.map((s) => emitStatement(s)).join("");
    const header = options?.preserveHeader?.trim() ?? "";
    let requireLine = "";
    if (req.size > 0) {
        // If header already has require["include"], merge remaining into a body require
        const needed = [...req].sort();
        if (header) {
            const headerHas = new Set([...header.matchAll(/"([^"]+)"/g)].map((m) => m[1].toLowerCase()));
            const missing = needed.filter((e) => !headerHas.has(e.toLowerCase()));
            if (missing.length) {
                requireLine = `require [${missing.map(quote).join(", ")}];\n`;
            }
        }
        else {
            requireLine = `require [${needed.map(quote).join(", ")}];\n`;
        }
    }
    const parts = [header, requireLine + body].filter((p) => p && p.length > 0);
    const out = parts.join(header ? "\n" : "");
    return out.endsWith("\n") ? out : `${out}\n`;
}
